import { useCallback } from 'react';

const CACHE_SIZE = 100; // Maximum number of cached responses
const CACHE_EXPIRY = 1000 * 60 * 60; // 1 hour in milliseconds

class MessageCache {
  constructor(maxSize = CACHE_SIZE) {
    this.cache = new Map();
    this.maxSize = maxSize;
  }

  get(key) {
    const item = this.cache.get(key);
    if (!item) return null;

    const now = Date.now();
    if (now - item.timestamp > CACHE_EXPIRY) {
      this.cache.delete(key);
      return null;
    }

    return item.value;
  }

  set(key, value) {
    if (this.cache.size >= this.maxSize) {
      // Remove oldest entry
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }

    this.cache.set(key, {
      value,
      timestamp: Date.now()
    });
  }

  clear() {
    this.cache.clear();
  }
}

const messageCache = new MessageCache();

export const useMessageCache = () => {
  const getCachedResponse = useCallback((input, context) => {
    const key = `${input}-${JSON.stringify(context)}`;
    return messageCache.get(key);
  }, []);

  const setCachedResponse = useCallback((input, context, response) => {
    const key = `${input}-${JSON.stringify(context)}`;
    messageCache.set(key, response);
  }, []);

  const clearCache = useCallback(() => {
    messageCache.clear();
  }, []);

  return {
    getCachedResponse,
    setCachedResponse,
    clearCache
  };
};
